import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, Menu, X, ChevronDown, LogOut, Package, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { categoryAPI } from '../api';

export default function Header() {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catOpen, setCatOpen] = useState(false);

  useEffect(() => { categoryAPI.getCategories().then(r => setCategories(r.data)).catch(() => {}); }, []);

  const cartCount = cart.items?.reduce((sum, i) => sum + i.quantity, 0) || 0;

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) { navigate(`/products?search=${encodeURIComponent(search.trim())}`); setSearch(''); }
  };

  const handleLogout = async () => { await logout(); setUserMenu(false); navigate('/'); };

  return (
    <header className="sticky top-0 z-50 overflow-x-hidden" data-testid="main-header">
      <div className="bg-[#0D5F59] text-white/80 text-xs py-1.5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-3 font-medium tracking-wide truncate" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <span className="truncate">4720 N High St, Columbus, OH 43214</span>
            <span className="opacity-40 hidden sm:inline">|</span>
            <a href="tel:7405648157" className="hidden sm:inline hover:text-white transition-colors">740-564-8157</a>
            <span className="opacity-40 hidden md:inline">|</span>
            <a href="mailto:mtzluis90@hotmail.com" className="hidden md:inline hover:text-white transition-colors">mtzluis90@hotmail.com</a>
            <span className="opacity-40 hidden md:inline">|</span>
            <a href="https://www.martinez-autoparts.com" target="_blank" rel="noopener noreferrer" className="hidden md:inline hover:text-white transition-colors font-semibold text-[#F97316]">www.martinez-autoparts.com</a>
          </div>
          <div className="hidden sm:flex gap-4">
            <Link to="/orders" className="hover:text-white transition-colors" data-testid="header-order-tracking">Order Tracking</Link>
            <span className="opacity-40">|</span>
            <Link to="/help" className="hover:text-white transition-colors">Help Center</Link>
          </div>
        </div>
      </div>
      <nav className="bg-[#0F766E] shadow-lg overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2 sm:gap-4">
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-white" data-testid="mobile-menu-btn">{mobileOpen ? <X size={24} /> : <Menu size={24} />}</button>
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
            <img src="/logo.png" alt="Martinez Auto Parts" className="h-14 w-auto object-contain" data-testid="logo-img" />
          </Link>
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl mx-4" data-testid="search-form">
            <div className="flex w-full">
              <input type="text" placeholder="Search parts, brands, OEM#..." value={search} onChange={e => setSearch(e.target.value)} className="flex-1 px-4 py-2.5 rounded-l text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]" data-testid="search-input" />
              <button type="submit" className="bg-[#F97316] hover:bg-[#EA580C] px-5 rounded-r transition-colors active:scale-95" data-testid="search-submit-btn"><Search size={18} className="text-white" /></button>
            </div>
          </form>
          <div className="flex items-center gap-1 sm:gap-2 ml-auto shrink-0">
            <div className="relative">
              <button onClick={() => user ? setUserMenu(!userMenu) : navigate('/login')} className="flex items-center gap-1.5 text-white hover:text-[#F97316] transition-colors px-2 py-1" data-testid="user-menu-btn">
                <User size={20} /><span className="hidden lg:block text-sm font-medium">{user ? user.name : 'Sign In'}</span>{user && <ChevronDown size={14} />}
              </button>
              {userMenu && user && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded shadow-xl border animate-slide-down z-50" data-testid="user-dropdown">
                  <div className="px-4 py-3 border-b"><p className="text-sm font-semibold text-gray-900">{user.name}</p><p className="text-xs text-gray-500">{user.email}</p></div>
                  <Link to="/orders" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-testid="dropdown-orders"><Package size={16} /> My Orders</Link>
                  {user.role === 'admin' && <Link to="/admin" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-testid="dropdown-admin"><Shield size={16} /> Admin Panel</Link>}
                  <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full border-t" data-testid="dropdown-logout"><LogOut size={16} /> Sign Out</button>
                </div>
              )}
            </div>
            <Link to="/cart" className="relative flex items-center gap-1.5 text-white hover:text-[#F97316] transition-colors px-2 py-1" data-testid="cart-link">
              <ShoppingCart size={22} />{cartCount > 0 && <span className="absolute -top-1 -right-1 bg-[#F97316] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">{cartCount}</span>}
              <span className="hidden lg:block text-sm font-medium">Cart</span>
            </Link>
          </div>
        </div>
        <div className="md:hidden px-4 pb-3">
          <form onSubmit={handleSearch} className="flex">
            <input type="text" placeholder="Search parts..." value={search} onChange={e => setSearch(e.target.value)} className="flex-1 px-3 py-2 rounded-l text-sm text-gray-900 focus:outline-none" data-testid="mobile-search-input" />
            <button type="submit" className="bg-[#F97316] px-4 rounded-r" data-testid="mobile-search-btn"><Search size={16} className="text-white" /></button>
          </form>
        </div>
      </nav>
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="hidden lg:flex items-center gap-1 py-2">
            <div className="relative" onMouseEnter={() => setCatOpen(true)} onMouseLeave={() => setCatOpen(false)}>
              <button className="flex items-center gap-1 text-sm font-semibold text-[#0F766E] hover:text-[#0D5F59] px-3 py-2 rounded transition-colors" data-testid="all-categories-btn"><Menu size={16} /> All Categories <ChevronDown size={14} /></button>
              {catOpen && (
                <div className="absolute left-0 top-full bg-white rounded-b shadow-xl border w-64 animate-slide-down z-40" data-testid="categories-dropdown">
                  {categories.map(cat => <Link key={cat.id} to={`/products?category=${cat.id}`} onClick={() => setCatOpen(false)} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#0F766E] hover:text-white transition-colors" data-testid={`category-link-${cat.id}`}>{cat.name}</Link>)}
                </div>
              )}
            </div>
            <div className="h-5 w-px bg-gray-300 mx-1" />
            {categories.slice(0, 7).map(cat => <Link key={cat.id} to={`/products?category=${cat.id}`} className="text-sm text-gray-700 hover:text-[#0F766E] font-medium px-3 py-2 rounded transition-colors whitespace-nowrap" data-testid={`nav-category-${cat.id}`}>{cat.name}</Link>)}
          </div>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 top-[100px] bg-white z-40 overflow-y-auto animate-fade-in" data-testid="mobile-menu">
          <div className="p-4 space-y-1">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Categories</p>
            {categories.map(cat => <Link key={cat.id} to={`/products?category=${cat.id}`} onClick={() => setMobileOpen(false)} className="block py-3 px-3 text-gray-800 hover:bg-gray-50 rounded text-sm font-medium">{cat.name}</Link>)}
            <div className="border-t pt-3 mt-3">
              {user ? (<>
                <Link to="/orders" onClick={() => setMobileOpen(false)} className="block py-3 px-3 text-gray-800 hover:bg-gray-50 rounded text-sm">My Orders</Link>
                {user.role === 'admin' && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block py-3 px-3 text-gray-800 hover:bg-gray-50 rounded text-sm">Admin Panel</Link>}
                <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="block py-3 px-3 text-red-600 text-sm w-full text-left">Sign Out</button>
              </>) : <Link to="/login" onClick={() => setMobileOpen(false)} className="block py-3 px-3 text-[#0F766E] font-medium text-sm">Sign In / Register</Link>}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
